import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { usePlan } from '../plan/usePlan';
import { EMPTY_FOLDERS } from '../entity/folders';
import type { Folders } from '../entity/folders';
import { folderHref } from './routes';

/**
 * The tag chips that are doors, and the ones that are not.
 *
 * A chip carries a NAME — that is what an arc's front-matter holds — so
 * whether it is a place has to be looked up. Curated names resolve; the ~6,800
 * ordinary tags do not, and those keep rendering as the labels they are. That
 * distinction is the point: a chip that looks clickable and is not teaches the
 * reader to stop trying.
 *
 * The folders come from the plan store rather than a prop because the chips
 * are leaves of three unrelated components, two of which never carried the
 * registries. Subscribing is a set insertion; the plan is read once either way.
 */

export function useFolders(): Folders {
  const { plan } = usePlan();
  return plan.status === 'ready' ? plan.plan.registries.folders : EMPTY_FOLDERS;
}

export function FolderLink({
  name,
  folders,
  className,
  style,
  children,
}: {
  name: string;
  folders: Folders;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const folder = folders.byName.get(name.trim().toLowerCase());
  if (!folder) {
    return (
      <span className={className} style={style}>
        {children}
      </span>
    );
  }
  return (
    <Link className={className} style={style} to={folderHref(folder.id)} title={`open ${folder.name}`}>
      {children}
    </Link>
  );
}
